import { artifactTypeAsResourceType } from '@cotera/client/v0/chat/artifacts';
import {
  getChatSeedStrategyForResourceType,
  hasChatSeedInSearchParams,
} from '../../chat-seed';

/**
 * Focus: one artifact filling the chat, as an address rather than a page.
 *
 * Expanding used to be a layout change and nothing else. The artifact filled
 * the body, but the state had no address — it could not be linked or reloaded —
 * and the bar above it still belonged to the conversation, including a Share
 * that grants the chat and everything in it, which is the wrong grant to offer
 * while one artifact is on screen.
 *
 * Addressing it fixed both. Making it a *second route* was the overshoot: two
 * sibling routes rendering the same `CocoChat` tore that tree down and rebuilt
 * it on every entry, which is why panel-local tabs had to be parked in a module
 * map to survive, why the seed had to be re-read from the URL, and why a
 * resource opened from a focused artifact could land on "not in this chat".
 * One route with the artifact in a trailing segment keeps the address and drops
 * the teardown: focus is a mode the chat is in, and `CocoChat` already took it
 * as an optional prop.
 *
 * Every move in and out of focus `replace`s. Focus is a display state, not a
 * place you travelled to — the way it never was when it was a layout change —
 * so it leaves no history behind it and Back still means "leave this chat". A
 * reader who has focused six artifacts in a row presses Back once to get out,
 * not six times.
 *
 * Nested under the chat rather than hung off the resource's own page, because
 * an artifact is not always a resource: charts, dashboards and workflow steps
 * have no page of their own, and the chat is what gives every artifact type an
 * id worth resolving.
 */
export const ARTIFACT_FOCUS_VIEW_SEGMENT = 'artifacts';

/**
 * The artifact a chat's location is focused on, or `null` for the conversation.
 *
 * The chat route matches everything under `/chats/:chatId` so that entering
 * focus is a param change on one mounted route rather than a swap between two.
 * That makes the tail this function's to read: anything that is not exactly
 * `artifacts/<id>` is the conversation, including the bare segment and any
 * deeper path, so a malformed address degrades to the chat instead of focusing
 * on nothing.
 */
export const focusedArtifactIdFromChatSplat = (
  splat: string | undefined
): string | null => {
  if (splat === undefined || splat === '') {
    return null;
  }
  const segments = splat.split('/');
  const [segment, artifactId] = segments;
  if (
    segments.length !== 2 ||
    segment !== ARTIFACT_FOCUS_VIEW_SEGMENT ||
    artifactId === undefined ||
    artifactId === ''
  ) {
    return null;
  }
  return artifactId;
};

/** `/:orgSlug/chats/:chatId/artifacts/:artifactId`. */
export const artifactFocusViewPath = (params: {
  orgSlug: string;
  chatId: string;
  artifactId: string;
}): string =>
  `/${params.orgSlug}/chats/${params.chatId}/${ARTIFACT_FOCUS_VIEW_SEGMENT}/${params.artifactId}`;

/** The chat a focus view belongs to, for the exit control. */
export const chatPathForArtifactFocusView = (params: {
  orgSlug: string;
  chatId: string;
}): string => `/${params.orgSlug}/chats/${params.chatId}`;

/**
 * The query string a route change *within* one chat has to carry.
 *
 * A seeded chat has no messages and nothing saved on the server: the artifact on
 * screen is there because the URL asked for it, and the seed is re-read from
 * these params on every mount. A bare path throws that away, so the chat that
 * lands has an empty artifact list — which is how opening a workflow from
 * Automations and pressing expand reported the workflow was not in this chat.
 *
 * Empty for a chat with no seed, where the artifacts come from the conversation
 * and the URL carries nothing worth keeping. The seed clears itself once the
 * chat has messages, so this stops applying exactly when it stops being true.
 */
const chatSeedSearch = (searchParams: URLSearchParams): string =>
  hasChatSeedInSearchParams(searchParams) ? `?${searchParams.toString()}` : '';

/**
 * The seed that reproduces *this* artifact, for one the chat cannot produce on
 * its own.
 *
 * The chat's own seed covers exactly one artifact — the one it was opened with.
 * Everything else in the panel is either recorded on the conversation or held
 * only in the panel's own state: a sibling tab opened from a Library artifact, a
 * resource opened from a workflow, an agent's `@`-mentioned references. Those
 * survive nothing, and a chat that has never been saved cannot record them at
 * all ({@link artifactRegistrationFor} needs a conversation to register
 * against), so they stay panel-local for as long as the chat is a seed.
 *
 * A cold load is where that bites. Entering focus no longer tears the panel
 * down, so what it is holding stays held — but the address names one artifact,
 * and a reload or a shared link has only the conversation plus the URL to
 * rebuild the list from. Without a seed of its own, a panel-local artifact
 * arrives at an address that cannot name it, and the view correctly reports
 * that it is not in the chat. Opening a workflow from Automations never showed
 * this: that workflow *is* its chat's seed.
 *
 * Rather than a second mechanism for putting an artifact in a URL, this reuses
 * the one that already exists. A seed strategy is exactly "how a resource is
 * encoded into a chat's address", and the registry already maps every
 * artifact-backed resource type to one. Types with no strategy — a chart, a
 * workflow node's config — have no resource behind them to encode, so this
 * returns `null` and {@link artifactFocusViewHref} falls back to the chat's own
 * seed rather than withholding the page.
 */
const seedSearchForArtifact = (
  artifactType: string,
  artifactId: string
): string | null => {
  const resourceType = artifactTypeAsResourceType(artifactType);
  if (resourceType === null) {
    return null;
  }
  const strategy = getChatSeedStrategyForResourceType(resourceType);
  if (strategy === undefined) {
    return null;
  }
  return `?${strategy.buildUrlSearchParams({ artifactId }).toString()}`;
};

/**
 * The address of one artifact's focus view.
 *
 * Always an address. Carrying a seed for the artifact is an upgrade on what the
 * chat's own seed could say, never a reason to withhold the page: an artifact
 * this cannot encode still opens focused exactly where it always did.
 */
export const artifactFocusViewHref = (params: {
  orgSlug: string;
  chatId: string;
  artifactId: string;
  searchParams: URLSearchParams;
  /** The artifact's type, so a panel-local one can carry its own seed. */
  artifactType?: string;
}): string => {
  const path = artifactFocusViewPath(params);
  // A saved chat records its artifacts, so the conversation reproduces them and
  // the URL needs to carry nothing. Only a seeded chat — one with nothing saved
  // to reproduce from — has to, so everything below this point has a seed.
  if (!hasChatSeedInSearchParams(params.searchParams)) {
    return path;
  }
  const chatSeed = `?${params.searchParams.toString()}`;
  // The chat is already addressed to this artifact — keep its seed exactly as
  // it stands, `new` marker and seed trigger included, rather than rebuilding a
  // plainer one and dropping what the strategy put there.
  if (params.searchParams.get('artifactId') === params.artifactId) {
    return `${path}${chatSeed}`;
  }
  const seed =
    params.artifactType === undefined
      ? null
      : seedSearchForArtifact(params.artifactType, params.artifactId);
  // Nothing encodes this artifact — a chart, a workflow node's config. Keep the
  // chat's own seed, which is what this carried before, so the page still opens
  // the way it always did rather than not at all.
  return `${path}${seed ?? chatSeed}`;
};

/** {@link chatPathForArtifactFocusView}, keeping the seed. */
export const chatHrefForArtifactFocusView = (params: {
  orgSlug: string;
  chatId: string;
  searchParams: URLSearchParams;
}): string =>
  `${chatPathForArtifactFocusView(params)}${chatSeedSearch(
    params.searchParams
  )}`;
